import { Plugin } from '@remirror/pm/state';
import { isElementDomNode } from '@remirror/core';
import { nodeAtCoords, getClosestSide, isNodeSlice, nearestBlockFromCoords } from '../utils';
export function dropIndicator(options) {
  let indicatorElement = null;
  let timeout = null;
  let scrollStartTime = null;
  let scrollAnimationFrame = null;
  const BASE_SCROLL_SPEED = 2;
  const MAX_SCROLL_SPEED = 15;
  const SCROLL_THRESHOLD = 200;
  const smoothScroll = (element, direction, distance) => {
    if (scrollAnimationFrame) {
      cancelAnimationFrame(scrollAnimationFrame);
    }
    if (!scrollStartTime) {
      scrollStartTime = Date.now();
    }
    const timeElapsed = Date.now() - scrollStartTime;
    const distanceFactor = Math.min(1, (SCROLL_THRESHOLD - Math.abs(distance)) / SCROLL_THRESHOLD);
    const currentSpeed = Math.min(BASE_SCROLL_SPEED * (1 + timeElapsed / 500) * distanceFactor, MAX_SCROLL_SPEED);
    const scroll = () => {
      element.scrollBy({
        top: direction * currentSpeed,
        behavior: 'auto',
      });
      scrollAnimationFrame = requestAnimationFrame(scroll);
    };
    scroll();
  };
  const stopScroll = () => {
    if (scrollAnimationFrame) {
      cancelAnimationFrame(scrollAnimationFrame);
      scrollAnimationFrame = null;
    }
    scrollStartTime = null;
  };
  const scheduleIndicatorHide = (delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (indicatorElement) {
        indicatorElement.style.opacity = '0';
      }
    }, delay);
  };
  return new Plugin({
    props: {
      handleDOMEvents: {
        dragover: (view, event) => {
          if (!view.editable || !view.dragging || !isNodeSlice(view.dragging.slice)) {
            return false;
          }
          const editorContainer = document.querySelector('.edit-editor-title');
          const { clientY } = event;
          const { top, height } = editorContainer.getBoundingClientRect();
          const relativeY = clientY - top;
          if (relativeY < SCROLL_THRESHOLD) {
            smoothScroll(editorContainer, -1, relativeY);
          } else if (relativeY > height - SCROLL_THRESHOLD) {
            smoothScroll(editorContainer, 1, relativeY - (height - SCROLL_THRESHOLD));
          } else {
            stopScroll();
          }
          const coords = { x: event.clientX, y: event.clientY };
          let targetNode = nodeAtCoords(coords, options.isDraggableBlock);
          let closestSide = targetNode && getClosestSide(targetNode, coords);
          if (!targetNode) {
            const { nearestBlock, side } = nearestBlockFromCoords(coords);
            targetNode = nearestBlock;
            closestSide = side;
          }
          // Looks better to have only one possible position for
          // the indicator between two nodes (otherwise there is two : one
          // at the bottom of the previous one, one at the top of the target)
          if (closestSide === 'top' && targetNode.previousElementSibling) {
            targetNode = targetNode.previousElementSibling;
            closestSide = 'bottom';
          }
          if ((closestSide === 'left' || closestSide === 'right') && targetNode?.parentNode?.matches('.column')) {
            targetNode = targetNode.parentNode;
          }
          if (!targetNode) {
            const { nearestBlock, side } = nearestBlockFromCoords(coords);
            targetNode = nearestBlock;
            closestSide = side;
          }
          if (!targetNode || !isElementDomNode(targetNode)) {
            indicatorElement.style.opacity = '0';
            return true;
          }
          const indicatorRect = {};
          const rect = targetNode.getBoundingClientRect();
          if (closestSide === 'top' || closestSide === 'bottom') {
            // Vertical
            indicatorRect.width = rect.width;
            indicatorRect.height = options.indicatorWidth;
            indicatorRect.left = rect.left;
            indicatorRect.top = closestSide === 'top' ? rect.top : rect.bottom;
          } else {
            // Horizontal
            indicatorRect.height = rect.height;
            indicatorRect.width = options.indicatorWidth;
            indicatorRect.left = closestSide === 'right' ? rect.right : rect.left - options.indicatorWidth;
            indicatorRect.top = rect.top;
          }
          indicatorElement.style.opacity = '1';
          Object.entries(indicatorRect).forEach(([key, value]) => {
            indicatorElement.style[key] = `${value}px`;
          });
        },
        dragend: () => {
          stopScroll();
          scheduleIndicatorHide(20);
        },
        drop: () => {
          stopScroll();
          scheduleIndicatorHide(20);
        },
        dragleave: (view, event) => {
          stopScroll();
          if (event.target === view.dom || !view.dom.contains(event.relatedTarget)) {
            scheduleIndicatorHide(20);
          }
        },
      },
    },
    view: () => {
      indicatorElement = document.createElement('div');
      indicatorElement.style.cssText = `
        position: absolute;
        z-index: 99;
        pointer-events: none;
        background-color: ${options.indicatorColor};
        transition: opacity ease-out 200ms`;
      if (options.indicatorClass) {
        indicatorElement.classList.add(options.indicatorClass);
      }
      document.body.appendChild(indicatorElement);
      return {
        destroy: () => {
          indicatorElement.remove();
          indicatorElement = null;
        },
      };
    },
  });
}
